import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Cancel as CancelIcon,
  Delete as DeletePlayerIcon,
} from '@mui/icons-material';
import { Player, playerService } from '@/services/playerService';
import { useQueryClient, useQuery } from 'react-query';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useScrollFab } from '@/hooks/useScrollFab';
import { useAppSelector } from '@/hooks/redux';
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier';
import PlayerCard from '@/components/players/PlayerCard';

const PlayersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const fabRef = useScrollFab(100);
  
  // Delete confirmation
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);

  // Fetch players with React Query - include org ID in key
  const { data: players = [], isLoading: loading, error: queryError } = useQuery(
    ['players', user?.currentOrganizationId],
    playerService.getPlayers,
    {
      enabled: !!user?.currentOrganizationId, // Only fetch if we have an org
      refetchOnMount: 'always', // Always fetch fresh data on mount
      refetchOnWindowFocus: true,
    }
  );

  const { isAtLimit, limits } = useSubscriptionTier();
  const playerLimitReached = isAtLimit('players', players.length);

  const error = queryError ? 'Failed to load players' : null;

  // Refresh players when organization changes
  useOrganizationChange(() => {
    queryClient.invalidateQueries(['players', user?.currentOrganizationId]);
  });

  // Add player handler
  const handleAddPlayer = () => {
    navigate('/players/new');
  };

  const handleDeleteClick = (player: Player) => {
    setPlayerToDelete(player);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!playerToDelete) return;

    try {
      setDeleteLoading(playerToDelete.id);
      setDeleteConfirmOpen(false);
      
      await playerService.deletePlayer(playerToDelete.id);
      
      console.log('🔄 PlayersPage: Player deleted, clearing schedule cache...');
      
      // Clear schedule cache to force refresh since player deletion removes them from all teams
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          const matches = Array.isArray(key) && key[0] === 'schedule';
          if (matches) {
            console.log('🗑️ PlayersPage: Removing cached schedule query:', key);
          }
          return matches;
        }
      });
      
      console.log('✅ PlayersPage: Schedule cache cleared');
      
      // Refresh players list
      queryClient.invalidateQueries(['players', user?.currentOrganizationId]);
      
      console.log(`Player ${playerToDelete.name} deleted successfully`);
      
    } catch (err: any) {
      console.error('Failed to delete player:', err);
      // Could add a snackbar notification here if needed
    } finally {
      setDeleteLoading(null);
      setPlayerToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPlayerToDelete(null);
  };

  if (loading) {
    return null;
  }

  return (
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 4 } }}>
      {/* Header */}
      <Box mb={4}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            Players
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Manage your players and their team assignments across multiple leagues
        </Typography>
        
        {/* Add Player Button - Desktop */}
        {!isMobile && players.length > 0 && (
          <Tooltip title={playerLimitReached ? `Maximum of ${limits.maxPlayers} players reached` : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddPlayer}
                size="large"
                disabled={playerLimitReached}
              >
                Add Player
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Players Grid */}
      {players.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'grey.300'
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No players yet
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4} maxWidth="sm" mx="auto">
            Get started by adding your first player. You can add their basic information, import their leagues, and assign them to teams using our step-by-step wizard.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPlayer}
            size="large"
          >
            Add Your First Player
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {players.map((player) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={player.id}>
              <PlayerCard
                player={player}
                onDelete={handleDeleteClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button - Mobile */}
      {isMobile && players.length > 0 && !playerLimitReached && (
        <Fab
          color="primary"
          ref={fabRef}
          aria-label="add player"
          onClick={handleAddPlayer}
          sx={{
            position: 'fixed',
            bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', // 56px nav + 16px margin + safe area
            right: 16,
            zIndex: 1000,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeletePlayerIcon />Delete Player</Box></DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{playerToDelete?.name}</strong>? 
            This will remove the player and all their team assignments. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleDeleteCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading === playerToDelete?.id}
            startIcon={deleteLoading === playerToDelete?.id ? <CircularProgress size={16} /> : undefined}
          >
            {deleteLoading === playerToDelete?.id ? 'Deleting...' : 'Delete Player'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlayersPage;
